/**
 * usePatchMePrefs — mutation for `PATCH /api/v1/me`.
 *
 * Used by Settings → Notifications / Privacy / Language and Settings →
 * Account (identity: name / sex). The mutation is optimistic: it patches the
 * cached `['me']` value immediately and only rolls back if the server rejects
 * the update.
 *
 * Merge-semantics live on the server: caller passes partial
 * notificationPrefs / privacyPrefs and the backend layers them over the
 * stored record + defaults. Identity fields (name → firstName, sex) are
 * replaced outright; `sex: null` clears it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';
import type {
  MeResponse,
  NotificationPrefs,
  PrivacyPrefs,
  LanguageCode,
} from './useMe';

export interface PatchMePrefsBody {
  language?: LanguageCode;
  notificationPrefs?: Partial<NotificationPrefs>;
  privacyPrefs?: Partial<PrivacyPrefs>;
  name?: string;
  sex?: 'MALE' | 'FEMALE' | null;
  publicProfileEnabled?: boolean;
}

export function usePatchMePrefs(): {
  mutate: (body: PatchMePrefsBody) => void;
  mutateAsync: (body: PatchMePrefsBody) => Promise<MeResponse>;
  isPending: boolean;
  isError: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();

  const m = useMutation<MeResponse, Error, PatchMePrefsBody, { previous: MeResponse | undefined }>({
    mutationFn: async (body) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/me', jwt, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`patch_me_failed: ${res.status} ${text}`);
      }
      return (await res.json()) as MeResponse;
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['me'] });
      const previous = qc.getQueryData<MeResponse>(['me']);
      if (previous) {
        const next: MeResponse = {
          ...previous,
          language: body.language ?? previous.language,
          languageCode: body.language ?? previous.languageCode,
          firstName: body.name ?? previous.firstName,
          sex: body.sex !== undefined ? body.sex : previous.sex,
          notificationPrefs: body.notificationPrefs
            ? { ...previous.notificationPrefs, ...body.notificationPrefs }
            : previous.notificationPrefs,
          privacyPrefs: body.privacyPrefs
            ? { ...previous.privacyPrefs, ...body.privacyPrefs }
            : previous.privacyPrefs,
          publicProfileEnabled: body.publicProfileEnabled !== undefined
            ? body.publicProfileEnabled
            : previous.publicProfileEnabled,
        };
        qc.setQueryData(['me'], next);
      }
      return { previous };
    },
    onError: (_err, _body, context) => {
      if (context?.previous) qc.setQueryData(['me'], context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return { mutate: m.mutate, mutateAsync: m.mutateAsync, isPending: m.isPending, isError: m.isError };
}
